import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("No session ID found");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
          credentials: "include"
        });
        
        if (!res.ok) {
          // Handle 404 or other errors
          const data = await res.json().catch(() => ({}));
          setStatus("error");
          setError(data.detail || "Transaction not found");
          return;
        }
        
        const data = await res.json();
        
        if (data.payment_status === "paid") {
          setStatus("success");
        } else if (data.status === "failed" || data.status === "expired") {
          setStatus("error");
          setError("Payment was not completed");
        } else {
          // Still pending, poll again (max 15 retries = 30 seconds)
          setRetryCount(prev => {
            if (prev >= 15) {
              setStatus("error");
              setError("Payment verification timed out. Please check your email for confirmation.");
              return prev;
            }
            setTimeout(checkStatus, 2000);
            return prev + 1;
          });
        }
      } catch (err) {
        console.error("Error checking payment status:", err);
        setStatus("error");
        setError("Failed to verify payment status");
      }
    };

    checkStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="payment-success-card">
        <CardContent className="pt-8 pb-6 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-600 mx-auto animate-spin" />
              <h1 className="mt-6 text-2xl font-bold text-slate-900">
                Confirming your payment...
              </h1>
              <p className="mt-2 text-slate-600">
                Please wait while we verify your subscription.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <h1 className="mt-6 text-2xl font-bold text-slate-900" data-testid="success-title">
                Payment Successful!
              </h1>
              <p className="mt-2 text-slate-600">
                Thank you for subscribing to nikosafe. Your account has been upgraded.
              </p>
              <div className="mt-8 space-y-3">
                <Link to="/dashboard">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-dashboard-btn">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/settings">
                  <Button variant="outline" className="w-full" data-testid="manage-subscription-btn">
                    Manage Subscription
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">!</span>
              </div>
              <h1 className="mt-6 text-2xl font-bold text-slate-900">
                Payment Issue
              </h1>
              <p className="mt-2 text-slate-600">
                {error || "There was a problem with your payment."}
              </p>
              <div className="mt-8">
                <Link to="/settings">
                  <Button className="w-full">Try Again</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
